import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Card,
} from 'antd';

import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import API from 'utils/api';
import { onSuccess, onError } from 'utils/handlers';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import NumericInput from 'components/NumericInput';
import FundamentosTransfer from './FundamentosTransfer';

const baseURI = 'tramites/plazos-maximos/';

const TablePlazoMax = ({
  selectedRowKey,
  callback,
}) => {
  const [formTable] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [unidadPlazo, setUnidadPlazo] = useState([]);
  const [tipoFicta, setTipoFicta] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await API.get(baseURI, {
        params: {
          plantilla_de_tramites: selectedRowKey,
        },
      });

      if (response?.status === 200) {
        setData(response.data);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const resPlazo = await API.get('/catalogos/unidades-de-plazo');
      setUnidadPlazo(resPlazo.data || []);
      const resFicta = await API.get('/catalogos/tipos-de-ficta');
      setTipoFicta(resFicta.data || []);

      if (selectedRowKey) {
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    fetchAll();

    return () => API.source.cancel;
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    formTable.resetFields();
    setVisibleAlert(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await formTable.validateFields();
      const values = formTable.getFieldsValue();
      const [key] = selectedRowKeys;
      if (selectedRowKey) {
        values.plantilla_de_tramites = selectedRowKey;
        if (!key) {
          const response = await API.post(baseURI, values);
          if (response?.status === 201) {
            onSuccess(response, 'Agregado correctamente');
            onCancel();
            await fetchData();
          }
        } else {
          const response = await API.put(`${baseURI}${key}/`, values);
          if (response?.status === 200) {
            onSuccess(response, 'Actualizado correctamente');
            onCancel();
            await fetchData();
          }
        }
      } else {
        const clone = [...data];
        if (!key) {
          clone.push({
            ...values,
            key: clone.length + 1,
          });
          setData(clone);
          callback(clone);
          onCancel();
        } else {
          const res = clone.map((item) => (item.key === key ? { ...values, key } : item));
          setData(res);
          callback(res);
          onCancel();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (selectedRowKey) {
        if (key) {
          const response = await API.delete(`${baseURI}${key}/`);
          if (response?.status === 204) {
            onSuccess(response, 'Eliminado correctamente');
            onCancel();
            await fetchData();
          }
        }
      } else {
        const clone = data.filter((item) => item.key !== key);
        const normalized = clone.map((item, idx) => ({ ...item, key: idx }));
        setData(normalized);
        callback(normalized);
        onCancel();
        setVisibleAlert(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickAdd = () => {
    setVisible(true);
    setSelectedRowKeys([]);
    formTable.resetFields();
  };
  const onClickEdit = async () => {
    try {
      setLoading(true);
      if (selectedRowKey) {
        const [key] = selectedRowKeys;
        const res = await API.get(`${baseURI}${key}`);
        const { fundamento_legal, ...values } = res.data;
        if (fundamento_legal?.length) {
          const promises = [];
          fundamento_legal.forEach((id) => {
            const response = API.get(`/configuracion/fundamentos-legales/${id}`);
            promises.push(response);
          });
          const responses = promises.length ? await Promise.all(promises) : [];
          const fundamentos = responses.map((fundamento) => fundamento.data);
          formTable.setFieldsValue({
            fundamento_legal,
            fundamento_legal_objects: fundamentos,
          });
        }
        formTable.setFieldsValue(values);
      } else {
        if (data?.fundamento_legal?.length) {
          const promises = [];
          data.fundamento_legal.forEach((id) => {
            const response = API.get(`/configuracion/fundamentos-legales/${id}`);
            promises.push(response);
          });
          const responses = promises.length ? await Promise.all(promises) : [];
          const fundamentos = responses.map((fundamento) => fundamento.data);
          formTable.setFieldsValue({
            fundamento_legal: data?.fundamento_legal,
            fundamento_legal_objects: fundamentos,
          });
        }
        formTable.setFieldsValue(data);
      }
      setVisible(true);
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    const _key = selectedRowKey ? record.id : record.key;
    setSelectedRowKeys([_key]);
    formTable.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Plazo Máximo',
      dataIndex: 'plazo_maximo',
      key: 'plazo_maximo',
      width: 150,
    },
    {
      titleText: 'Tipo de Ficta',
      dataIndex: 'tipo_de_ficta',
      key: 'tipo_ficta',
      width: 150,
      render: (val) => tipoFicta.find((item) => item.id === val)?.descripcion,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 300,
    },

  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            mobileColIndex={0}
            rowKey={selectedRowKey ? 'id' : 'key'}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            allowImport
            baseURI={baseURI}
          />
        ) : (
          <Card
            bordered={false}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
              />
             )}
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Plazo Máximo`}
          >
            <Form
              layout="vertical"
              form={formTable}
              onFinish={onFinish}
              scrollToFirstError
            >
              <Row gutter={10}>

                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="unidad_de_plazo_maximo"
                    label="Unidad del Plazo"
                  >
                    <Select dataSource={unidadPlazo} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>

                  <NumericInput
                    name="plazo_maximo"
                    label="Plazo Máximo"
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="tipo_de_ficta"
                    label="Tipo de Ficta"
                  >
                    <Select dataSource={tipoFicta} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                    hasFeedback
                  >
                    <Input.TextArea allowClear autoSize={{ minRows: 2, maxRows: 4 }} />
                  </Form.Item>
                </Col>
                <FundamentosTransfer formFundamento={formTable} />
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Row>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content="El Plazo Máximo seleccionado"
        />
      </Spin>
    </Row>
  );
};
TablePlazoMax.propTypes = {

  selectedRowKey: PropTypes.number,
  callback: PropTypes.func.isRequired,

};
TablePlazoMax.defaultProps = {
  selectedRowKey: null,
};

export default TablePlazoMax;
